import { ReactNode } from 'react';
import { AlertCircle, ReceiptText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { ROUTE } from '@/config';
import { useFetchMerchantPricingPlan } from '@/hooksApi';
import { formatWithThousandSeparator } from '@/utils';
import type { MerchantPricingTier, MerchantPricingTierPriced } from '@/types';
import PlanRates from './PlanRates';
import ReviewCard from './ReviewCard';
import ServiceRateGrid from './ServiceRateGrid';

/**
 * Settings > Pricing plan — the merchant's own tier standing, READ ONLY.
 *
 * Everything that moves the ladder lives on /v1/trusted; the merchant only sees
 * the number they are billed, the volume it came from, and when it moves next.
 *
 * NOT shown, and it must stay that way: min_spread_iqd, the tier's margin
 * floor. The API does not return it to this surface at all — see
 * MerchantPricingResource, which is an allow-list.
 */

const CARD = 'rounded-2xl border border-sd-border bg-sd-card shadow-sm';
// The rate card is the one thing a merchant opens this page to read, so it
// carries the brand tint rather than sitting in the same grey as the rest.
const HIGHLIGHT_CARD = 'rounded-2xl border border-sd-primary bg-sd-primary/10 shadow-sm';
const PRIMARY_BUTTON =
  'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity';
const DATE_FORMAT = 'DD MMM YYYY';

// `status` is a varchar on the backend precisely so a new lifecycle state is
// not a migration — an unknown one must degrade to something readable, so tone
// falls back to neutral and the label to the raw code via i18next defaultValue.
const STATUS_TONE: Record<string, string> = {
  active: 'border-green-200 bg-green-50 text-green-700',
  grace: 'border-blue-200 bg-blue-50 text-blue-700',
  pending_downgrade: 'border-amber-200 bg-amber-50 text-amber-700',
  // Negotiated per-account rates — above the ladder, not outside it.
  custom: 'border-purple-200 bg-purple-50 text-purple-700',
};
const NEUTRAL_TONE = 'border-sd-border bg-sd-muted text-sd-muted-foreground';

interface PageShellProps {
  children: ReactNode;
}

const PageShell = ({ children }: PageShellProps) => (
  <div className='sd-scope sd-font flex flex-col'>
    <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
      <SettingsBreadcrumb moduleName='pricing_plan' moduleRoute={ROUTE.SETTINGS_PRICING_PLAN} />
      {children}
    </div>
  </div>
);

// Money is an LTR quantity whatever the page direction — same <bdi dir='ltr'>
// the Advance COD cards use, or the IQD suffix lands on the wrong side.
const Money = ({ amount }: { amount: number }) => {
  const { t } = useTranslation();

  return (
    <bdi dir='ltr' className='tabular-nums'>
      {formatWithThousandSeparator(amount)} {String(t('iqd'))}
    </bdi>
  );
};

// The two halves of a divergence, side by side. Collapsing them to one number
// hides the point: committed is the plan they were placed on, effective is the
// one they are charged at today.
const PlanFacet = ({
  label,
  plan,
}: {
  label: string;
  plan: MerchantPricingTier | MerchantPricingTierPriced;
}) => (
  <div className='rounded-xl border border-sd-border bg-sd-card p-3'>
    <p className='m-0 text-xs font-medium text-sd-muted-foreground'>{label}</p>
    <p className='m-0 mt-1 text-sm font-semibold text-sd-foreground'>{plan.title}</p>
    {/* Only the plan in force is priced. The agreed tier is named, never
        costed — see MerchantPricingTier.base_price_iqd. */}
    {'base_price_iqd' in plan && typeof plan.base_price_iqd === 'number' ? (
      <p className='m-0 mt-0.5 text-sm text-sd-muted-foreground'>
        <Money amount={plan.base_price_iqd} />
      </p>
    ) : null}
  </div>
);

interface EmptyCardProps {
  icon: ReactNode;
  title: string;
  body: string;
  children?: ReactNode;
}

const EmptyCard = ({ icon, title, body, children }: EmptyCardProps) => (
  <div className={`${CARD} p-8 text-center`}>
    <div className='mb-3 flex justify-center text-sd-muted-foreground'>{icon}</div>
    <p className='m-0 mb-1 text-sm font-medium text-sd-foreground'>{title}</p>
    <p className='m-0 text-xs text-sd-muted-foreground'>{body}</p>
    {children}
  </div>
);

const PricingPlanSection = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useFetchMerchantPricingPlan();

  const header = (
    <div>
      <h1 className='m-0 text-xl font-semibold text-sd-foreground'>{t('pricing_plan')}</h1>
      <p className='mt-1 text-sm text-sd-muted-foreground'>{t('pricing_plan_description')}</p>
    </div>
  );

  if (isLoading) {
    return (
      <PageShell>
        <div className='space-y-3'>
          {[0, 1].map((block) => (
            <div key={block} className={`${CARD} p-8`}>
              <div className='animate-pulse space-y-4'>
                {['w-3/4', 'w-1/2', 'w-5/6'].map((width) => (
                  <div key={width} className={`h-4 rounded bg-sd-muted ${width}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </PageShell>
    );
  }

  // A failed read and "no plan" are different answers and must not share a
  // screen: telling a merchant they are on standard pricing when the request
  // actually errored is a lie about what they are charged.
  if (isError) {
    return (
      <PageShell>
        {header}
        <EmptyCard
          icon={<AlertCircle className='h-10 w-10' />}
          title={String(t('pricing_plan_error_title'))}
          body={String(t('pricing_plan_error_body'))}
        >
          <button type='button' onClick={() => refetch()} className={`${PRIMARY_BUTTON} mt-4`}>
            {t('retry')}
          </button>
        </EmptyCard>
      </PageShell>
    );
  }

  // 200 + null object. This is the NORM — there are zero granting assignments
  // in production today — so it gets a designed answer, not a shrug.
  if (!data) {
    return (
      <PageShell>
        {header}
        <EmptyCard
          icon={<ReceiptText className='h-10 w-10' />}
          title={String(t('pricing_plan_none_title'))}
          body={String(t('pricing_plan_none_body'))}
        />
      </PageShell>
    );
  }

  const { committed_plan: committed, effective_plan: effective } = data;

  // The merchant's COUNTED band (`data.current_band`) is deliberately not read
  // here any more. It is a statement about the merchant, not about the price,
  // and it already has a home on the ladder card, which lights the segment the
  // merchant is standing in. Putting it beside the rate made the card assert a
  // relationship between the two that does not hold — see the subtitle below.

  // uid, not price: two tiers can share a price after a repricing, and it is
  // the PLAN that diverged.
  const diverged = Boolean(committed && effective && committed.uid !== effective.uid);

  // 🔴 THE SUBTITLE DESCRIBES THE PRICE'S OWN BAND, NEVER THE COUNTED ONE.
  //
  // The card reads as one sentence: "your rates — for X – Y orders / month —
  // 4,250". Pairing the rate with the band the merchant's VOLUME falls in is
  // false whenever they are not on the plan that volume would place them on,
  // and that is the ordinary state: production assignments came from the
  // owner's sheet rather than from volume, and a merchant whose volume drops
  // stays on the old plan until an evaluation runs.
  //
  // An earlier attempt suppressed the subtitle during grace and divergence —
  // which is exactly the state this is NOT. Two merchants sitting active and
  // undiverged on the 1,250 rung, counting 200 and 3,100, still read "for
  // 150 – 500 orders / month" beside a 1,250-rung price.
  //
  // The counted band keeps its home on the ladder card, where it is a
  // statement about the MERCHANT rather than about the price.
  // 🔴 A BAND ONLY EXISTS IF THE PLAN HAS ONE. A custom/negotiated plan carries
  // no volume bounds, and formatting a null through the thousand separator
  // renders a DASH — which is how this subtitle came to read "For – – – orders
  // / month." on a real merchant's page. Absent bounds means there is no band
  // to describe, so fall through to the generic hint instead of describing one
  // with dashes where the numbers should be.
  const planBand =
    !effective || effective.volume_from === null || effective.volume_from === undefined
      ? null
      : effective.volume_to === null
        ? String(
            t('pricing_plan_band_top', { from: formatWithThousandSeparator(effective.volume_from) }),
          )
        : String(
            t('pricing_plan_band', {
              from: formatWithThousandSeparator(effective.volume_from),
              to: formatWithThousandSeparator(effective.volume_to),
            }),
          );
  const rateSubtitle = planBand ?? String(t('your_rate_today_hint'));

  // Negotiated rates: no ladder, so Card B (volume, review, grace) is a
  // story about a mechanism that does not apply and is not rendered.
  const isCustom = data.type === 'custom';

  return (
    <PageShell>
      {header}

      {/* CARD A — the number they are billed. Highlighted per the BOXY-232
          design: this is the one thing on the page a merchant came to read,
          and it sat in the same grey card as everything else. */}
      <section className={HIGHLIGHT_CARD}>
        <div className='flex flex-wrap items-center justify-between gap-3 border-b border-sd-primary/40 px-5 py-4'>
          <div>
            <h3 className='text-base font-semibold'>{t('pricing_plan_current_rates')}</h3>
            <p className='mt-0.5 text-xs text-sd-muted-foreground'>{rateSubtitle}</p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='inline-flex items-center rounded-full bg-sd-foreground px-2.5 py-1 text-[11px] font-bold tracking-wide text-sd-primary'>
              {String(t('pricing_plan_current_badge'))}
            </span>
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
                STATUS_TONE[data.status] ?? NEUTRAL_TONE
              }`}
            >
              {String(t(`pricing_status_${data.status}`, { defaultValue: data.status }))}
            </span>
          </div>
        </div>

        <div className='space-y-4 p-5'>
          {effective ? (
            <div className='space-y-3'>
              {/* BOXY-232 — the rate grid, one cell per service x destination
                  scope. It supersedes the single-service views below because a
                  merchant pays on every service they can ship with, not only
                  the one their TIER is assigned to: the tier book holds exactly
                  one plan per merchant, so it could never answer for both Boxy
                  Select and Boxy Save. These come from the pricing cascade.

                  The two older renders stay as fallbacks, not as dead code: a
                  merchant on a custom plan, or an environment where nothing
                  resolves through the cascade, still has a rate to show. */}
              {(data.service_rates?.length ?? 0) > 0 ? (
                <ServiceRateGrid services={data.service_rates ?? []} />
              ) : (effective.rates?.length ?? 0) > 1 ? (
                <PlanRates rates={effective.rates ?? []} />
              ) : (
                <p className='m-0 text-3xl font-semibold text-sd-foreground'>
                  <Money amount={effective.base_price_iqd} />
                </p>
              )}
              <p className='m-0 text-sm text-sd-muted-foreground'>
                {isCustom
                  ? t('pricing_plan_custom_note')
                  : t('pricing_plan_per_order_on', { plan: effective.title })}
              </p>
            </div>
          ) : (
            <p className='m-0 text-sm text-sd-muted-foreground'>{t('pricing_plan_no_rate_yet')}</p>
          )}

          {diverged && committed && effective ? (
            <div className='space-y-3 rounded-xl border border-sd-border bg-sd-muted/30 p-4'>
              <p className='m-0 text-xs text-sd-muted-foreground'>
                {t('pricing_plan_committed_vs_effective')}
              </p>
              <div className='grid gap-3 sm:grid-cols-2'>
                <PlanFacet label={String(t('pricing_plan_agreed'))} plan={committed} />
                <PlanFacet label={String(t('pricing_plan_billed_today'))} plan={effective} />
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* CARD B — the ladder story. A custom plan has no ladder: volume and
          review rows would imply a mechanism that will never move this
          merchant's price, so the whole card is not rendered. */}
      {isCustom ? null : <ReviewCard data={data} />}
    </PageShell>
  );
};

export default PricingPlanSection;
